import React, { useState, useEffect } from 'react';
import supabase from '../../utils/supabaseClient';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Chip,
  Stack,
  Alert,
  Snackbar,
  Divider,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  Edit as EditIcon
} from '@mui/icons-material';

const BucketManager = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filePreview, setFilePreview] = useState(null);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const BUCKET_NAME = 'gallery';

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      
      // List all files in the bucket
      const { data, error } = await supabase
        .storage
        .from(BUCKET_NAME)
        .list('public');
      
      if (error) throw error;
      
      // Get URLs for all files
      const filesWithUrls = await Promise.all(
        data.map(async (file) => {
          const { data: urlData } = supabase
            .storage
            .from(BUCKET_NAME)
            .getPublicUrl(`public/${file.name}`);
          
          // Get metadata from database if it exists
          const { data: metaData } = await supabase
            .from('images')
            .select('*')
            .ilike('url', `%${file.name}%`)
            .maybeSingle();
          
          return {
            ...file,
            url: urlData.publicUrl,
            metadata: metaData || null
          };
        })
      );
      
      setFiles(filesWithUrls);
    } catch (error) {
      console.error('Error fetching files:', error);
      showSnackbar('Error fetching files', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = e.target.files;
    if (files) {
      setSelectedFiles(files);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      showSnackbar('Please select a file to upload', 'error');
      return;
    }

    try {
      setUploading(true);
      
      // Upload file to Supabase Storage directly
      const fileNames = selectedFiles.map((file, index) => `${Date.now()}-${index}-${file.name}`);
      const uploadPromises = fileNames.map((fileName, index) => {
        return supabase
          .storage
          .from(BUCKET_NAME)
          .upload(`public/${fileName}`, selectedFiles[index], {
            cacheControl: '3600',
            upsert: false
          });
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      
      // Get the public URLs
      const urlPromises = uploadResults.map((result, index) => {
        return supabase
          .storage
          .from(BUCKET_NAME)
          .getPublicUrl(`public/${fileNames[index]}`);
      });
      
      const urlResults = await Promise.all(urlPromises);
      
      // Crear objetos de datos para la inserción
      const imageDatas = urlResults.map((result, index) => {
        // Asegurarse de que la URL es correcta (corregir posible discrepancia en el dominio)
        const correctUrl = result.data.publicUrl.replace(
          /hwbclfumdwinckxjhxf\.supabase\.co/,
          'hmhclfubmdginrbxjhvf.supabase.co'
        );
        
        return {
          title: 'Untitled',
          description: '',
          url: correctUrl,
          tags: tags
          // Omitimos completamente user_id para evitar problemas de clave foránea
        };
      });
      
      // Intentar insertar en la base de datos
      const { data: insertedData, error: dbError } = await supabase
        .from('images')
        .insert(imageDatas);
      
      if (dbError) throw dbError;
      
      // Reset form and close dialog
      resetForm();
      setOpenUploadDialog(false);
      
      // Refresh file list
      fetchFiles();
      
      showSnackbar('Files uploaded successfully', 'success');
    } catch (error) {
      console.error('Error uploading file:', error);
      showSnackbar(`Error uploading file: ${error.message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!fileToDelete) return;
    
    try {
      // Delete file from storage
      const { error: storageError } = await supabase
        .storage
        .from(BUCKET_NAME)
        .remove([`public/${fileToDelete.name}`]);
      
      if (storageError) throw storageError;
      
      // Delete metadata from database if it exists
      if (fileToDelete.metadata?.id) {
        const { error: dbError } = await supabase
          .from('images')
          .delete()
          .eq('id', fileToDelete.metadata.id);
        
        if (dbError) throw dbError;
      }
      
      // Close dialog and refresh list
      setOpenDeleteDialog(false);
      setFileToDelete(null);
      fetchFiles();
      
      showSnackbar('File deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting file:', error);
      showSnackbar('Error deleting file', 'error');
    }
  };

  const resetForm = () => {
    setSelectedFiles([]);
    setFilePreview(null);
    setTags([]);
    setTagInput('');
  };

  const handleOpenUploadDialog = () => {
    resetForm();
    setOpenUploadDialog(true);
  };

  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  const handleOpenDeleteDialog = (file) => {
    setFileToDelete(file);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setFileToDelete(null);
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !tags.includes(tagInput.trim())) {
      setTags([...tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  const isImage = (filename) => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'];
    const extension = filename.split('.').pop().toLowerCase();
    return imageExtensions.includes(extension);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2">
          Storage Bucket Manager
        </Typography>
        
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />} 
            onClick={fetchFiles}
            sx={{ mr: 2 }}
          >
            Refresh
          </Button>
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />} 
            onClick={handleOpenUploadDialog}
          >
            Upload New File
          </Button>
        </Box>
      </Box>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : files.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No files found in bucket
          </Typography>
          <Button 
            variant="contained" 
            startIcon={<CloudUploadIcon />} 
            onClick={handleOpenUploadDialog}
            sx={{ mt: 2 }}
          >
            Upload Your First File
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {files.map((file) => (
            <Grid item xs={12} sm={6} md={4} key={file.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {isImage(file.name) ? (
                  <CardMedia
                    component="img"
                    height="200"
                    image={file.url}
                    alt={file.metadata?.title || file.name}
                    sx={{ objectFit: 'cover' }}
                  />
                ) : (
                  <Box sx={{ 
                    height: 200, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'background.elevated' 
                  }}>
                    <ImageIcon sx={{ fontSize: 80, opacity: 0.5 }} />
                  </Box>
                )}
                
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography variant="h6" component="h3" gutterBottom noWrap>
                    {file.metadata?.title || file.name}
                  </Typography>
                  
                  {file.metadata?.description && (
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {file.metadata.description.length > 100 
                        ? `${file.metadata.description.substring(0, 100)}...` 
                        : file.metadata.description}
                    </Typography>
                  )}
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Size: {(file.metadata?.size || 0) / 1024 < 1024 
                        ? `${((file.metadata?.size || 0) / 1024).toFixed(2)} KB` 
                        : `${((file.metadata?.size || 0) / (1024 * 1024)).toFixed(2)} MB`}
                    </Typography>
                  </Box>
                  
                  {file.metadata?.tags && file.metadata.tags.length > 0 && (
                    <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                      {file.metadata.tags.map((tag, index) => (
                        <Chip key={index} label={tag} size="small" />
                      ))}
                    </Stack>
                  )}
                </CardContent>
                
                <Divider />
                
                <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton 
                    color="primary" 
                    aria-label="edit file" 
                    sx={{ mr: 1 }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton 
                    color="error" 
                    aria-label="delete file"
                    onClick={() => handleOpenDeleteDialog(file)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
      
      {/* Upload Dialog */}
      <Dialog open={openUploadDialog} onClose={handleCloseUploadDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
              fullWidth
              sx={{ mb: 3, p: 1.5, border: '1px dashed' }}
            >
              Select Files
              <input
                type="file"
                multiple
                hidden
                onChange={handleFileChange}
              />
            </Button>
            
            {filePreview && (
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <img 
                  src={filePreview} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px', 
                    borderRadius: '8px' 
                  }} 
                />
              </Box>
            )}
            
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Tags
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TextField
                  label="Add tag"
                  size="small"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  sx={{ flexGrow: 1, mr: 1 }}
                />
                <Button 
                  variant="outlined" 
                  onClick={handleAddTag}
                  disabled={!tagInput.trim()}
                >
                  Add
                </Button>
              </Box>
              
              {tags.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
                  {tags.map((tag, index) => (
                    <Chip 
                      key={index} 
                      label={tag} 
                      onDelete={() => handleRemoveTag(tag)} 
                    />
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUploadDialog}>Cancel</Button>
          <Button 
            onClick={handleUpload} 
            variant="contained" 
            color="primary"
            disabled={!selectedFiles.length || uploading}
            startIcon={uploading ? <CircularProgress size={20} /> : <CloudUploadIcon />}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{fileToDelete?.metadata?.title || fileToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BucketManager;
